import React, { useState, useEffect, useRef } from 'react';
import { Home, TrendingUp, DollarSign, Calendar, MapPin, Layers, Edit2, X, BarChart3, Download, Upload, Share2, Copy, Check } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const FLOOR_PLANS = {
  carlton: { name: 'Carlton', sqft: 1793 },
  norris: { name: 'Norris', sqft: 1962 },
  auburn: { name: 'Auburn', sqft: 2270 }
};

// Configuration
const BEAR_MARKET_MODE = true; // Set to false in bull market to allow predictions above list price

export default function HousingTracker() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showGraph, setShowGraph] = useState(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    floorPlan: 'carlton',
    address: '',
    unitType: 'interior',
    status: 'sold',
    saleDate: '',
    salePrice: '',
    listPrice: '',
    originalListPrice: '',
    originalListDate: ''
  });

  useEffect(() => {
    loadProperties();
  }, []);

  const compressData = (data) => {
    // Simple compression by removing unnecessary whitespace and using short keys
    return btoa(JSON.stringify(data));
  };

  const decompressData = (compressed) => {
    try {
      return JSON.parse(atob(compressed));
    } catch {
      return null;
    }
  };

  const loadFromURL = () => {
    const params = new URLSearchParams(window.location.search);
    const dataParam = params.get('data');
    if (dataParam) {
      const decompressed = decompressData(dataParam);
      if (decompressed && Array.isArray(decompressed)) {
        return decompressed;
      }
    }
    return null;
  };

  const loadProperties = async () => {
    try {
      // First, try to load from URL (highest priority)
      const urlData = loadFromURL();
      if (urlData) {
        setProperties(urlData);
        saveAllProperties(urlData);
        setLoading(false);
        return;
      }

      // Try window.storage first (for Claude.ai), then localStorage (for deployed sites)
      if (typeof window.storage !== 'undefined') {
        const keys = await window.storage.list('property:');
        if (keys && keys.keys) {
          const props = await Promise.all(
            keys.keys.map(async (key) => {
              try {
                const result = await window.storage.get(key);
                return result ? JSON.parse(result.value) : null;
              } catch {
                return null;
              }
            })
          );
          const filtered = props.filter(p => p !== null);
          if (filtered.length > 0) {
            setProperties(filtered);
            setLoading(false);
            return;
          }
        }
      } else {
        // Use localStorage for deployed sites
        const stored = localStorage.getItem('housing-properties');
        if (stored) {
          setProperties(JSON.parse(stored));
          setLoading(false);
          return;
        }
      }

      // If no data found, try to load default housing-data.json
      try {
        const response = await fetch('/housing-data.json');
        if (response.ok) {
          const defaultData = await response.json();
          if (Array.isArray(defaultData) && defaultData.length > 0) {
            setProperties(defaultData);
            saveAllProperties(defaultData);
          }
        }
      } catch (error) {
        console.log('No default data file found');
      }
    } catch (error) {
      console.log('No existing properties found');
    } finally {
      setLoading(false);
    }
  };

  const saveAllProperties = (props) => {
    // Save to localStorage for deployed sites
    try {
      localStorage.setItem('housing-properties', JSON.stringify(props));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const saveProperty = async (property) => {
    try {
      // Try window.storage first (for Claude.ai)
      if (typeof window.storage !== 'undefined') {
        await window.storage.set(`property:${property.id}`, JSON.stringify(property));
      }
    } catch (error) {
      console.error('Error saving property:', error);
    }
  };

  const deleteProperty = async (id) => {
    try {
      // Try window.storage first (for Claude.ai)
      if (typeof window.storage !== 'undefined') {
        await window.storage.delete(`property:${id}`);
      }
      const newProperties = properties.filter(p => p.id !== id);
      setProperties(newProperties);
      saveAllProperties(newProperties);
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.address) return;
    
    if (formData.status === 'sold' && (!formData.saleDate || !formData.salePrice)) return;
    if (formData.status === 'listed' && !formData.listPrice) return;
    
    const newProperty = {
      id: Date.now().toString(),
      floorPlan: formData.floorPlan,
      address: formData.address,
      unitType: formData.unitType,
      status: formData.status,
      saleDate: formData.status === 'sold' ? formData.saleDate : null,
      salePrice: formData.status === 'sold' ? parseFloat(formData.salePrice) : null,
      listPrice: formData.status === 'listed' ? parseFloat(formData.listPrice) : null,
      originalListPrice: formData.originalListPrice ? parseFloat(formData.originalListPrice) : null,
      originalListDate: formData.originalListDate || null,
      createdAt: new Date().toISOString()
    };

    await saveProperty(newProperty);
    const newProperties = [...properties, newProperty];
    setProperties(newProperties);
    saveAllProperties(newProperties);
    
    setFormData({
      floorPlan: 'carlton',
      address: '',
      unitType: 'interior',
      status: 'sold',
      saleDate: '',
      salePrice: '',
      listPrice: '',
      originalListPrice: '',
      originalListDate: ''
    });
  };

  const handleEdit = (property) => {
    setEditingId(property.id);
    setFormData({
      floorPlan: property.floorPlan,
      address: property.address,
      unitType: property.unitType,
      status: property.status,
      saleDate: property.saleDate || '',
      salePrice: property.salePrice || '',
      listPrice: property.listPrice || ''
    });
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdate = async () => {
    if (!formData.address) return;
    
    if (formData.status === 'sold' && (!formData.saleDate || !formData.salePrice)) return;
    if (formData.status === 'listed' && !formData.listPrice) return;

    const updatedProperty = {
      id: editingId,
      floorPlan: formData.floorPlan,
      address: formData.address,
      unitType: formData.unitType,
      status: formData.status,
      saleDate: formData.status === 'sold' ? formData.saleDate : null,
      salePrice: formData.status === 'sold' ? parseFloat(formData.salePrice) : null,
      listPrice: formData.status === 'listed' ? parseFloat(formData.listPrice) : null,
      originalListPrice: formData.originalListPrice ? parseFloat(formData.originalListPrice) : null,
      originalListDate: formData.originalListDate || null,
      createdAt: properties.find(p => p.id === editingId)?.createdAt || new Date().toISOString()
    };

    await saveProperty(updatedProperty);
    const newProperties = properties.map(p => p.id === editingId ? updatedProperty : p);
    setProperties(newProperties);
    saveAllProperties(newProperties);
    
    setEditingId(null);
    setFormData({
      floorPlan: 'carlton',
      address: '',
      unitType: 'interior',
      status: 'sold',
      saleDate: '',
      salePrice: '',
      listPrice: '',
      originalListPrice: '',
      originalListDate: ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      floorPlan: 'carlton',
      address: '',
      unitType: 'interior',
      status: 'sold',
      saleDate: '',
      salePrice: '',
      listPrice: '',
      originalListPrice: '',
      originalListDate: ''
    });
  };

  const getPredictionExplanation = (prediction, currentListPrice) => {
    if (!prediction) return null;

    const parts = [];
    
    // Base data
    parts.push(`Based on ${prediction.count} comparable sale${prediction.count !== 1 ? 's' : ''}`);
    
    // List-to-sale ratio explanation (using ORIGINAL list prices)
    if (prediction.avgListToSaleRatio && prediction.dataQuality.withListPrice > 0) {
      const ratioPercent = (prediction.avgListToSaleRatio * 100).toFixed(1);
      parts.push(`homes sold for ${ratioPercent}% of their original list price on average (${prediction.dataQuality.withListPrice} sale${prediction.dataQuality.withListPrice !== 1 ? 's' : ''} with original list data)`);
      
      if (currentListPrice) {
        const ratioPrediction = Math.round(currentListPrice * prediction.avgListToSaleRatio);
        parts.push(`suggesting ${formatCurrency(ratioPrediction)} for current list`);
      }
    }
    
    // Days on market adjustment
    if (prediction.currentDaysOnMarket && prediction.avgDaysOnMarket) {
      if (prediction.currentDaysOnMarket > prediction.avgDaysOnMarket * 1.2) {
        parts.push(`property has been on market ${prediction.currentDaysOnMarket} days (${Math.round((prediction.currentDaysOnMarket / prediction.avgDaysOnMarket - 1) * 100)}% longer than average), suggesting price adjustment may be needed`);
      } else if (prediction.currentDaysOnMarket < prediction.avgDaysOnMarket * 0.8) {
        parts.push(`property is relatively new to market (${prediction.currentDaysOnMarket} days vs ${prediction.avgDaysOnMarket} avg)`);
      } else {
        parts.push(`typically selling in ${prediction.avgDaysOnMarket} days`);
      }
    } else if (prediction.avgDaysOnMarket) {
      parts.push(`typically selling in ${prediction.avgDaysOnMarket} days`);
    }
    
    // Trend explanation
    if (prediction.count >= 2) {
      if (prediction.trend === 'up') {
        parts.push('with an upward price trend');
      } else if (prediction.trend === 'down') {
        parts.push('with a downward price trend');
      }
    }
    
    // Model weights explanation (if multiple models used)
    if (prediction.modelWeights && (prediction.modelWeights.listToSale > 0 || prediction.modelWeights.daysOnMarket > 0)) {
      const modelParts = [];
      if (prediction.modelWeights.timeTrend > 0) {
        modelParts.push(`${Math.round(prediction.modelWeights.timeTrend * 100)}% time trend`);
      }
      if (prediction.modelWeights.listToSale > 0) {
        modelParts.push(`${Math.round(prediction.modelWeights.listToSale * 100)}% list-to-sale ratio`);
      }
      if (prediction.modelWeights.daysOnMarket > 0) {
        modelParts.push(`${Math.round(prediction.modelWeights.daysOnMarket * 100)}% days-on-market`);
      }
      if (modelParts.length > 0) {
        parts.push(`prediction weighted by: ${modelParts.join(', ')}`);
      }
    }
    
    // Bear market cap explanation
    if (BEAR_MARKET_MODE && currentListPrice && prediction.predicted >= currentListPrice) {
      parts.push('(capped at list price due to bear market conditions)');
    }
    
    return parts.join('; ') + '.';
  };

  const getEstimatedURLSize = () => {
    const compressed = compressData(properties);
    const baseURL = window.location.origin + window.location.pathname;
    return baseURL.length + compressed.length + 6; // +6 for "?data="
  };

  const canUseURLSharing = () => {
    return getEstimatedURLSize() < 8000; // Conservative limit
  };

  const generateShareLink = () => {
    const compressed = compressData(properties);
    const baseURL = window.location.origin + window.location.pathname;
    const shareURL = `${baseURL}?data=${compressed}`;
    
    navigator.clipboard.writeText(shareURL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => {
      alert('Failed to copy link. Please copy manually: ' + shareURL);
    });
  };

  const exportData = () => {
    const dataStr = JSON.stringify(properties, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `housing-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (Array.isArray(imported)) {
            setProperties(imported);
            saveAllProperties(imported);
            alert(`Successfully imported ${imported.length} properties!`);
          } else {
            alert('Invalid file format. Please upload a valid JSON file.');
          }
        } catch (error) {
          alert('Error reading file. Please make sure it\'s a valid JSON file.');
        }
      };
      reader.readAsText(file);
    }
    event.target.value = '';
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to delete ALL properties? This cannot be undone!')) {
      setProperties([]);
      saveAllProperties([]);
      alert('All data cleared!');
    }
  };

  const calculatePrediction = (floorPlan, unitType, listPrice = null, listDate = null) => {
    const soldProperties = properties.filter(
      p => p.floorPlan === floorPlan && p.status === 'sold' && p.unitType === unitType
    );

    if (soldProperties.length === 0) return null;

    const sorted = soldProperties
      .map(p => ({
        ...p,
        timestamp: new Date(p.saleDate).getTime()
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    const avgPrice = sorted.reduce((sum, p) => sum + p.salePrice, 0) / sorted.length;

    // Calculate list-to-sale ratio using ORIGINAL list price (not final/sale price)
    const propsWithOriginalListPrice = sorted.filter(p => p.originalListPrice && p.originalListPrice > 0);
    let avgListToSaleRatio = null;
    let listToSaleRatios = [];
    
    if (propsWithOriginalListPrice.length > 0) {
      listToSaleRatios = propsWithOriginalListPrice.map(p => p.salePrice / p.originalListPrice);
      avgListToSaleRatio = listToSaleRatios.reduce((sum, r) => sum + r, 0) / listToSaleRatios.length;
    }

    // Calculate days on market for properties with listing date
    const propsWithListDate = sorted.filter(p => p.originalListDate && p.saleDate);
    let avgDaysOnMarket = null;
    let daysOnMarketData = [];
    
    if (propsWithListDate.length > 0) {
      daysOnMarketData = propsWithListDate.map(p => {
        const listDate = new Date(p.originalListDate).getTime();
        const saleDate = new Date(p.saleDate).getTime();
        return {
          days: (saleDate - listDate) / (1000 * 60 * 60 * 24),
          salePrice: p.salePrice,
          originalListPrice: p.originalListPrice
        };
      });
      avgDaysOnMarket = Math.round(daysOnMarketData.reduce((sum, d) => sum + d.days, 0) / daysOnMarketData.length);
    }

    // Calculate current days on market for the listing
    let currentDaysOnMarket = null;
    if (listDate) {
      currentDaysOnMarket = Math.round((new Date() - new Date(listDate)) / (1000 * 60 * 60 * 24));
    }

    if (sorted.length >= 2) {
      const firstDate = new Date(sorted[0].saleDate);
      const lastDate = new Date(sorted[sorted.length - 1].saleDate);
      const monthsDiff = (lastDate - firstDate) / (1000 * 60 * 60 * 24 * 30.44);

      const n = sorted.length;
      const sumX = sorted.reduce((sum, p, i) => sum + i, 0);
      const sumY = sorted.reduce((sum, p) => sum + p.salePrice, 0);
      const sumXY = sorted.reduce((sum, p, i) => sum + (i * p.salePrice), 0);
      const sumXX = sorted.reduce((sum, p, i) => sum + (i * i), 0);

      const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
      const projectedPrice = sorted[sorted.length - 1].salePrice + slope;

      const prices = sorted.map(p => p.salePrice);
      const variance = prices.reduce((sum, p) => sum + Math.pow(p - avgPrice, 2), 0) / n;
      const stdDev = Math.sqrt(variance);
      const coefficientOfVariation = stdDev / avgPrice;

      let confidence = 'high';
      if (coefficientOfVariation > 0.15) confidence = 'medium';
      if (coefficientOfVariation > 0.25) confidence = 'low';

      const trendWeight = Math.min(n / 5, 1);
      let predicted = (projectedPrice * trendWeight) + (avgPrice * (1 - trendWeight));

      // Multi-variable prediction blending
      const predictions = [];
      const weights = [];

      // Model 1: Time-based trend prediction
      predictions.push(predicted);
      weights.push(0.3); // Base weight for time trend

      // Model 2: List-to-sale ratio prediction (if available)
      if (avgListToSaleRatio && listPrice) {
        const ratioPrediction = listPrice * avgListToSaleRatio;
        predictions.push(ratioPrediction);
        
        // Weight increases with more data
        const ratioWeight = Math.min(propsWithOriginalListPrice.length / 4, 0.5);
        weights.push(ratioWeight);
      }

      // Model 3: Days on market adjustment (if available)
      if (avgDaysOnMarket && currentDaysOnMarket && daysOnMarketData.length >= 2) {
        // If property has been on market longer than average, predict lower sale price
        // If shorter than average, predict higher
        const daysDelta = currentDaysOnMarket - avgDaysOnMarket;
        const daysAdjustmentFactor = 1 - (daysDelta / avgDaysOnMarket) * 0.1; // Max 10% adjustment
        const daysAdjustedPrice = avgPrice * Math.max(0.85, Math.min(1.15, daysAdjustmentFactor));
        
        predictions.push(daysAdjustedPrice);
        const daysWeight = Math.min(propsWithListDate.length / 4, 0.3);
        weights.push(daysWeight);
      }

      // Normalize weights and calculate blended prediction
      const totalWeight = weights.reduce((sum, w) => sum + w, 0);
      const normalizedWeights = weights.map(w => w / totalWeight);
      
      predicted = predictions.reduce((sum, pred, i) => sum + (pred * normalizedWeights[i]), 0);

      // Bear market adjustment: cap predicted price at current list price
      if (BEAR_MARKET_MODE && listPrice && predicted > listPrice) {
        predicted = listPrice;
      }

      return {
        predicted: Math.round(predicted),
        average: Math.round(avgPrice),
        count: sorted.length,
        trend: slope > 0 ? 'up' : slope < 0 ? 'down' : 'stable',
        monthlyChange: monthsDiff > 0 ? Math.round((slope / monthsDiff) * 100) / 100 : 0,
        confidence: confidence,
        priceRange: {
          low: Math.round(predicted - stdDev),
          high: Math.round(predicted + stdDev)
        },
        slope: slope,
        data: sorted,
        avgListToSaleRatio: avgListToSaleRatio,
        avgDaysOnMarket: avgDaysOnMarket,
        currentDaysOnMarket: currentDaysOnMarket,
        dataQuality: {
          withListPrice: propsWithOriginalListPrice.length,
          withListDate: propsWithListDate.length,
          total: sorted.length
        },
        modelWeights: {
          timeTrend: normalizedWeights[0] || 0,
          listToSale: normalizedWeights[1] || 0,
          daysOnMarket: normalizedWeights[2] || 0
        }
      };
    }

    let predicted = Math.round(avgPrice);
    
    // Use list-to-sale ratio if available (based on ORIGINAL list prices)
    if (avgListToSaleRatio && listPrice) {
      predicted = Math.round(listPrice * avgListToSaleRatio);
    }
    
    // Bear market adjustment: cap predicted price at current list price
    if (BEAR_MARKET_MODE && listPrice && predicted > listPrice) {
      predicted = listPrice;
    }

    return {
      predicted: predicted,
      average: Math.round(avgPrice),
      count: sorted.length,
      trend: 'neutral',
      confidence: 'low',
      data: sorted,
      avgListToSaleRatio: avgListToSaleRatio,
      avgDaysOnMarket: avgDaysOnMarket,
      currentDaysOnMarket: currentDaysOnMarket,
      dataQuality: {
        withListPrice: propsWithOriginalListPrice.length,
        withListDate: propsWithListDate.length,
        total: sorted.length
      }
    };
  };

  const getGraphData = (floorPlan, unitType) => {
    const prediction = calculatePrediction(floorPlan, unitType);
    if (!prediction || !prediction.data) return null;

    const historicalData = prediction.data.map((p, idx) => ({
      date: new Date(p.saleDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      timestamp: new Date(p.saleDate).getTime(),
      price: p.salePrice,
      address: p.address,
      type: 'actual',
      index: idx
    }));

    if (prediction.slope !== undefined && historicalData.length >= 2) {
      const lastPoint = historicalData[historicalData.length - 1];
      const lastTimestamp = lastPoint.timestamp;
      const futureTimestamp = lastTimestamp + (30.44 * 24 * 60 * 60 * 1000);
      
      historicalData.push({
        date: new Date(futureTimestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        timestamp: futureTimestamp,
        price: prediction.predicted,
        address: 'Predicted',
        type: 'predicted',
        index: historicalData.length
      });
    }

    return historicalData;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-gray-600">Loading properties...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Home className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-800">Housing Sales Tracker</h1>
              {editingId && (
                <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-medium">
                  Editing Property
                </span>
              )}
            </div>
            
            <div className="flex flex-wrap gap-2">
              {canUseURLSharing() && properties.length > 0 && (
                <button
                  onClick={generateShareLink}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                  title="Copy shareable link to clipboard"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Share Link'}
                </button>
              )}
              {properties.length > 0 && (
                <button
                  onClick={exportData}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  title="Export data as JSON file"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                title="Import data from JSON file"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={importData}
                className="hidden"
              />
              {properties.length > 0 && (
                <button
                  onClick={clearAllData}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  title="Delete all data"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {!canUseURLSharing() && properties.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              <strong>Note:</strong> You have {properties.length} properties. URL sharing works best with fewer than 30 properties. 
              Use the Export button to share larger datasets.
              {getEstimatedURLSize() > 0 && ` (Current size: ~${Math.round(getEstimatedURLSize() / 1000)}KB)`}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Floor Plan
                </label>
                <select
                  value={formData.floorPlan}
                  onChange={(e) => setFormData({ ...formData, floorPlan: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {Object.entries(FLOOR_PLANS).map(([key, plan]) => (
                    <option key={key} value={key}>
                      {plan.name} ({plan.sqft.toLocaleString()} sq ft)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Type
                </label>
                <select
                  value={formData.unitType}
                  onChange={(e) => setFormData({ ...formData, unitType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="interior">Interior Unit</option>
                  <option value="end">End Unit</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="sold">Sold</option>
                  <option value="listed">Currently Listed</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main Street"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            {formData.status === 'sold' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sale Date
                  </label>
                  <input
                    type="date"
                    value={formData.saleDate}
                    onChange={(e) => setFormData({ ...formData, saleDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sale Price
                  </label>
                  <input
                    type="number"
                    value={formData.salePrice}
                    onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                    placeholder="450000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original List Price <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.originalListPrice}
                    onChange={(e) => setFormData({ ...formData, originalListPrice: e.target.value })}
                    placeholder="475000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original List Date <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.originalListDate}
                    onChange={(e) => setFormData({ ...formData, originalListDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {formData.status === 'listed' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current List Price
                  </label>
                  <input
                    type="number"
                    value={formData.listPrice}
                    onChange={(e) => setFormData({ ...formData, listPrice: e.target.value })}
                    placeholder="475000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original List Date <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="date"
                    value={formData.originalListDate}
                    onChange={(e) => setFormData({ ...formData, originalListDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original List Price <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.originalListPrice}
                    onChange={(e) => setFormData({ ...formData, originalListPrice: e.target.value })}
                    placeholder="490000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {editingId ? (
              <div className="flex gap-3">
                <button
                  onClick={handleUpdate}
                  className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
                >
                  Update Property
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-6 bg-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleSubmit}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
              >
                Add Property
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {Object.entries(FLOOR_PLANS).map(([key, plan]) => {
            const interiorPrediction = calculatePrediction(key, 'interior');
            const endPrediction = calculatePrediction(key, 'end');
            
            return (
              <div key={key} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-4">{plan.sqft.toLocaleString()} sq ft</p>
                
                <div className="space-y-4">
                  <div className="border-b pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">Interior Units</span>
                      </div>
                      {interiorPrediction && interiorPrediction.count >= 2 && (
                        <button
                          onClick={() => setShowGraph({ floorPlan: key, unitType: 'interior' })}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <BarChart3 className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                    {interiorPrediction ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <TrendingUp className={`w-5 h-5 ${
                            interiorPrediction.trend === 'up' ? 'text-green-500' : 
                            interiorPrediction.trend === 'down' ? 'text-red-500' : 'text-gray-500'
                          }`} />
                          <div>
                            <p className="text-xs text-gray-500">Predicted Price</p>
                            <p className="text-xl font-bold text-indigo-600">
                              {formatCurrency(interiorPrediction.predicted)}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600">
                          <p>Avg: {formatCurrency(interiorPrediction.average)}</p>
                          <p>{interiorPrediction.count} sale{interiorPrediction.count !== 1 ? 's' : ''}</p>
                          {interiorPrediction.avgListToSaleRatio && (
                            <p>List/Sale Ratio: {(interiorPrediction.avgListToSaleRatio * 100).toFixed(1)}%</p>
                          )}
                          {interiorPrediction.avgDaysOnMarket && (
                            <p>Avg Days on Market: {interiorPrediction.avgDaysOnMarket}</p>
                          )}
                          {interiorPrediction.confidence && (
                            <p className={`font-medium ${
                              interiorPrediction.confidence === 'high' ? 'text-green-600' :
                              interiorPrediction.confidence === 'medium' ? 'text-yellow-600' : 'text-orange-600'
                            }`}>
                              {interiorPrediction.confidence} confidence
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No sales data</p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium text-gray-600">End Units</span>
                      </div>
                      {endPrediction && endPrediction.count >= 2 && (
                        <button
                          onClick={() => setShowGraph({ floorPlan: key, unitType: 'end' })}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <BarChart3 className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                    {endPrediction ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <TrendingUp className={`w-5 h-5 ${
                            endPrediction.trend === 'up' ? 'text-green-500' : 
                            endPrediction.trend === 'down' ? 'text-red-500' : 'text-gray-500'
                          }`} />
                          <div>
                            <p className="text-xs text-gray-500">Predicted Price</p>
                            <p className="text-xl font-bold text-indigo-600">
                              {formatCurrency(endPrediction.predicted)}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-gray-600">
                          <p>Avg: {formatCurrency(endPrediction.average)}</p>
                          <p>{endPrediction.count} sale{endPrediction.count !== 1 ? 's' : ''}</p>
                          {endPrediction.avgListToSaleRatio && (
                            <p>List/Sale Ratio: {(endPrediction.avgListToSaleRatio * 100).toFixed(1)}%</p>
                          )}
                          {endPrediction.avgDaysOnMarket && (
                            <p>Avg Days on Market: {endPrediction.avgDaysOnMarket}</p>
                          )}
                          {endPrediction.confidence && (
                            <p className={`font-medium ${
                              endPrediction.confidence === 'high' ? 'text-green-600' :
                              endPrediction.confidence === 'medium' ? 'text-yellow-600' : 'text-orange-600'
                            }`}>
                              {endPrediction.confidence} confidence
                            </p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No sales data</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {showGraph && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full p-8 max-h-screen overflow-y-auto">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {FLOOR_PLANS[showGraph.floorPlan].name} - {showGraph.unitType === 'end' ? 'End' : 'Interior'} Units
                  </h2>
                  <p className="text-gray-600">Price Trend Over Time</p>
                </div>
                <button
                  onClick={() => setShowGraph(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={getGraphData(showGraph.floorPlan, showGraph.unitType)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => {
                      return [formatCurrency(value), props.payload.address];
                    }}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke="#4f46e5" 
                    strokeWidth={2}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      if (payload.type === 'predicted') {
                        return (
                          <circle 
                            cx={cx} 
                            cy={cy} 
                            r={6} 
                            fill="#10b981" 
                            stroke="#fff" 
                            strokeWidth={2}
                          />
                        );
                      }
                      return (
                        <circle 
                          cx={cx} 
                          cy={cy} 
                          r={5} 
                          fill="#4f46e5" 
                          stroke="#fff" 
                          strokeWidth={2}
                        />
                      );
                    }}
                    name="Sale Price"
                  />
                </LineChart>
              </ResponsiveContainer>
              
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-indigo-600 rounded-full"></div>
                  <span>Actual Sales</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                  <span>Predicted Price</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-gray-800">All Properties</h2>
          
          {properties.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center text-gray-500">
              No properties added yet. Add your first property above!
            </div>
          ) : (
            properties
              .sort((a, b) => {
                if (a.status === 'listed' && b.status === 'sold') return -1;
                if (a.status === 'sold' && b.status === 'listed') return 1;
                if (a.saleDate && b.saleDate) {
                  return new Date(b.saleDate) - new Date(a.saleDate);
                }
                return new Date(b.createdAt) - new Date(a.createdAt);
              })
              .map((property) => {
                const plan = FLOOR_PLANS[property.floorPlan];
                const prediction = property.status === 'listed' ? 
                  calculatePrediction(property.floorPlan, property.unitType, property.listPrice, property.originalListDate) : null;
                
                return (
                  <div key={property.id} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-gray-800">{plan.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            property.status === 'sold' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {property.status === 'sold' ? 'Sold' : 'Listed'}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {property.unitType === 'end' ? 'End Unit' : 'Interior Unit'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <MapPin className="w-4 h-4" />
                          <p>{property.address}</p>
                        </div>
                        <p className="text-sm text-gray-500">{plan.sqft.toLocaleString()} sq ft</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(property)}
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteProperty(property.id)}
                          className="text-red-500 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {property.status === 'sold' ? (
                        <>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500">Sale Date</p>
                              <p className="font-semibold text-gray-700">{formatDate(property.saleDate)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500">Sale Price</p>
                              <p className="font-semibold text-gray-700">{formatCurrency(property.salePrice)}</p>
                            </div>
                          </div>
                          {property.originalListPrice && (
                            <>
                              <div className="flex items-center gap-2">
                                <DollarSign className="w-5 h-5 text-blue-400" />
                                <div>
                                  <p className="text-xs text-gray-500">Original List Price</p>
                                  <p className="font-semibold text-gray-700">{formatCurrency(property.originalListPrice)}</p>
                                  <p className="text-xs text-gray-500">
                                    {((property.salePrice / property.originalListPrice) * 100).toFixed(1)}% of list
                                  </p>
                                </div>
                              </div>
                              {property.originalListDate && (
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-5 h-5 text-blue-400" />
                                  <div>
                                    <p className="text-xs text-gray-500">Original List Date</p>
                                    <p className="font-semibold text-gray-700">{formatDate(property.originalListDate)}</p>
                                    <p className="text-xs text-gray-500">
                                      {Math.round((new Date(property.saleDate) - new Date(property.originalListDate)) / (1000 * 60 * 60 * 24))} days on market
                                    </p>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-xs text-gray-500">Current List Price</p>
                              <p className="font-semibold text-gray-700">{formatCurrency(property.listPrice)}</p>
                            </div>
                          </div>
                          {property.originalListDate && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-5 h-5 text-blue-400" />
                              <div>
                                <p className="text-xs text-gray-500">Listed Since</p>
                                <p className="font-semibold text-gray-700">{formatDate(property.originalListDate)}</p>
                                <p className="text-xs text-gray-500">
                                  {Math.round((new Date() - new Date(property.originalListDate)) / (1000 * 60 * 60 * 24))} days on market
                                </p>
                              </div>
                            </div>
                          )}
                          {property.originalListPrice && property.originalListPrice !== property.listPrice && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-5 h-5 text-blue-400" />
                              <div>
                                <p className="text-xs text-gray-500">Original List Price</p>
                                <p className="font-semibold text-gray-700">{formatCurrency(property.originalListPrice)}</p>
                                <p className="text-xs text-gray-500">
                                  {((property.listPrice / property.originalListPrice - 1) * 100).toFixed(1)}% price change
                                </p>
                              </div>
                            </div>
                          )}
                          {prediction && (
                            <div className="col-span-full">
                              <div className="flex items-start gap-2 mt-2 p-3 bg-indigo-50 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-xs text-gray-500 mb-1">Predicted Sale Price</p>
                                  <p className="font-semibold text-indigo-600 text-lg">
                                    {formatCurrency(prediction.predicted)}
                                    <span className={`ml-2 text-xs ${
                                      prediction.confidence === 'high' ? 'text-green-600' :
                                      prediction.confidence === 'medium' ? 'text-yellow-600' : 'text-orange-600'
                                    }`}>
                                      ({prediction.confidence} confidence)
                                    </span>
                                  </p>
                                  {prediction.priceRange && (
                                    <p className="text-xs text-gray-500 mb-2">
                                      Range: {formatCurrency(prediction.priceRange.low)} - {formatCurrency(prediction.priceRange.high)}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-600 leading-relaxed">
                                    {getPredictionExplanation(prediction, property.listPrice)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })
          )}
        </div>
      </div>
    </div>
  );
}
